import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { fetchWithAuth } from '../utils/api';

// Eye toggle used to show/hide the password while typing.
const EyeIcon = ({ off }) => off ? (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
) : (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, hasRole, user } = useAuth();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });

  // Where to land after signing in: the page the user originally asked for,
  // or the dashboard.
  const destination = location.state?.from || '/on/dashboard';

  const updateSelectedCorp = async (corpId) => {
    try {
      const corp = await fetchWithAuth(`/corps/${corpId}`);
      localStorage.setItem('selectedCorpData', JSON.stringify(corp));
    } catch {}
  };

  useEffect(() => {
    if (user) navigate(destination, { replace: true });
    // Session-expiry and similar notices are informational, not errors.
    const msg = localStorage.getItem('loginMessage');
    if (msg) { setNotice(msg); localStorage.removeItem('loginMessage'); }
  }, [user, navigate, destination]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const data = await login(credentials.username, credentials.password);
      const tokenData = JSON.parse(atob(data.access_token.split('.')[1]));
      if (!hasRole('super') && tokenData.corpid) {
        localStorage.setItem('selectedCorpId', tokenData.corpid);
        await updateSelectedCorp(tokenData.corpid);
      }
      if (hasRole('employee') && tokenData.empid) {
        localStorage.setItem('employeeId', tokenData.empid);
      }
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        {/* One Nexus brand mark */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0 auto 32px' }}>
          <svg viewBox="0 0 100 100" width="48" height="48" aria-hidden="true">
            <g>
              <path d="M 83.8 40.9 A 35 35 0 1 1 59.1 16.2" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt"/>
              <circle cx="74.7" cy="25.3" r="8" fill="var(--brand-blue)"/>
              <path d="M 37 65 L 37 45 A 13 13 0 0 1 63 45 L 63 65" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt"/>
            </g>
          </svg>
          <span style={{
            fontFamily: 'var(--font-wordmark)',
            fontSize: '28px',
            fontWeight: 800,
            letterSpacing: '-1px',
            lineHeight: 1,
          }}>
            <span style={{ color: 'var(--brand-blue)' }}>One</span>
            <span style={{ color: 'var(--brand-navy)' }}> Nexus</span>
          </span>
        </div>

        <h1>Welcome</h1>
        <p style={{
          margin: '12px 0 32px',
          fontFamily: 'var(--font-sans)',
          fontSize: '15px',
          color: 'var(--body-text)',
          lineHeight: 1.55,
        }}>
          Sign in to your account to continue
        </p>

        {notice && (
          <div role="status" style={{
            padding: '12px 16px', marginBottom: '16px',
            background: 'var(--info-bg)', border: '1px solid var(--info-border)',
            color: 'var(--info)', borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-sans)', fontSize: '14px', textAlign: 'left',
          }}>
            {notice}
          </div>
        )}
        {error && <div className="error-message" role="alert">{error}</div>}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="login-identifier">Access code, username, or email</label>
            <input
              id="login-identifier"
              type="text"
              name="username"
              value={credentials.username}
              onChange={handleInputChange}
              placeholder="Access code, username, or email"
              autoComplete="username"
              disabled={loading}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
                required
                style={{ paddingRight: '42px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
                style={{
                  position: 'absolute', right: '6px', top: '50%',
                  transform: 'translateY(calc(-50% + 3px))',
                  background: 'transparent', border: 'none', padding: '6px',
                  color: 'var(--muted)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                }}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
          </div>
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div style={{
          marginTop: '28px',
          display: 'flex', justifyContent: 'center',
          fontFamily: 'var(--font-sans)', fontSize: '14px',
        }}>
          <Link to="/on/password-reset?mode=forgot" style={{ color: 'var(--coral)', textDecoration: 'none', fontWeight: 500 }}
            onMouseOver={e => e.target.style.textDecoration = 'underline'}
            onMouseOut={e => e.target.style.textDecoration = 'none'}>
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
